import type {
  VeeraContextualThreadInput,
  VeeraContextualThreadEntrySurface,
} from "@/lib/veeraContextualThreads";
import {
  buildVeeraThreadOpener,
  type VeeraThreadOpenerEvidenceStrength,
  type VeeraThreadOpenerResult,
} from "@/lib/veeraThreadOpeners";
import { buildVeeraContextualTopicId } from "@/lib/veeraContextualThreads";
import { buildVeeraThreadTitle } from "@/lib/veeraThreadTitles";
import {
  parseVeeraContextualEntryTransport,
  serializeVeeraContextualEntryTransport,
  type LeaderVeeraContextualEntryTransport,
} from "../../supabase/functions/_shared/veeraContextualEntry.ts";

export type LeaderVeeraContextualEntrySurface = Extract<
  VeeraContextualThreadEntrySurface,
  "today" | "insights" | "reflection"
>;

export interface LeaderVeeraContextualEntryInput {
  surface: LeaderVeeraContextualEntrySurface;
  leaderId: string;
  primaryEntityId: string;
  topicTitle: string;
  topicFingerprint: string;
  sourceLabel?: string | null;
  sourceId?: string | null;
  carryOverSummary: string;
  whyItMattersNow: string;
  evidenceStrength: VeeraThreadOpenerEvidenceStrength;
  question?: string | null;
}

export type LeaderVeeraContextualEntry = LeaderVeeraContextualEntryTransport & {
  contextualThreadInput: VeeraContextualThreadInput;
  assistantOpener: VeeraThreadOpenerResult;
};

function normalizeText(value: string | null | undefined, fallback = "") {
  const normalized = value?.replace(/\s+/g, " ").trim();
  return normalized ? normalized : fallback;
}

function normalizeTopicFingerprint(value: string | null | undefined) {
  const normalized = value?.replace(/\s+/g, " ").trim().toLowerCase();
  if (!normalized) return "unknown";
  return normalized
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-") || "unknown";
}

function normalizeSourceLabel(surface: LeaderVeeraContextualEntrySurface, value?: string | null) {
  const normalized = normalizeText(value);
  if (normalized) return normalized;

  switch (surface) {
    case "today":
      return "From Today";
    case "insights":
      return "From Insights";
    case "reflection":
      return "From Reflection";
  }

  return "From Today";
}

export function buildLeaderVeeraContextualEntry(
  params: LeaderVeeraContextualEntryInput,
): LeaderVeeraContextualEntry {
  const threadTitle = buildVeeraThreadTitle({
    role: "leader",
    entrySurface: params.surface,
    topicTitle: normalizeText(params.topicTitle, normalizeText(params.sourceLabel, "Coach Veera")),
  });
  const sourceLabel = normalizeSourceLabel(params.surface, params.sourceLabel);
  const topicFingerprint = normalizeTopicFingerprint(params.topicFingerprint);
  const contextualTopicId = buildVeeraContextualTopicId({
    role: "leader",
    entrySurface: params.surface,
    primaryEntityId: normalizeText(params.primaryEntityId, "unknown"),
    topicFingerprint,
  });

  return {
    role: "leader",
    surface: params.surface,
    threadTitle,
    sourceLabel,
    topicFingerprint,
    contextualThreadInput: {
      role: "leader",
      entrySurface: params.surface,
      primaryEntityId: normalizeText(params.primaryEntityId, "unknown"),
      topicFingerprint,
      contextualTopicId,
      sourceId: params.sourceId?.trim() ? params.sourceId.trim() : undefined,
    },
    assistantOpener: buildVeeraThreadOpener({
      role: "leader",
      entrySurface: params.surface,
      sourceLabel,
      carryOverSummary: params.carryOverSummary,
      whyItMattersNow: params.whyItMattersNow,
      evidenceStrength: params.evidenceStrength,
      question: params.question,
    }),
  };
}

export function serializeLeaderVeeraContextualEntry(entry: LeaderVeeraContextualEntry) {
  return serializeVeeraContextualEntryTransport(entry);
}

function isSurface(value: unknown): value is LeaderVeeraContextualEntrySurface {
  return value === "today" || value === "insights" || value === "reflection";
}

export function parseLeaderVeeraContextualEntry(
  raw: string | null | undefined,
): LeaderVeeraContextualEntry | null {
  if (!raw) return null;

  try {
    const parsed = parseVeeraContextualEntryTransport(raw);
    if (!parsed || parsed.role !== "leader" || !isSurface(parsed.surface)) return null;

    return buildLeaderVeeraContextualEntry({
      surface: parsed.surface,
      leaderId: normalizeText(parsed.contextualThreadInput?.primaryEntityId, "unknown"),
      primaryEntityId: normalizeText(parsed.contextualThreadInput?.primaryEntityId, "unknown"),
      topicTitle: normalizeText(parsed.threadTitle, "Coach Veera"),
      topicFingerprint: normalizeText(parsed.topicFingerprint ?? parsed.contextualThreadInput?.topicFingerprint, "unknown"),
      sourceLabel: normalizeText(parsed.sourceLabel),
      sourceId: normalizeText(parsed.contextualThreadInput?.sourceId) || null,
      carryOverSummary: normalizeText(parsed.assistantOpener?.carryOverSummary, ""),
      whyItMattersNow: normalizeText(parsed.assistantOpener?.whyItMattersNow, ""),
      evidenceStrength: parsed.assistantOpener?.shouldAskQuestion ? "strong" : "thin",
      question: normalizeText(parsed.assistantOpener?.question),
    });
  } catch {
    return null;
  }
}
